import Phaser from 'phaser';

// Зона на земле с эффектом на игрока. Не двигается, живёт до явного destroy
// или истечения lifetimeMs (если задан — с fade-out).
//
// Рендер: цветной Arc (default) ИЛИ спрайт (если задан textureKey). Origin
// (0.5, 0.5) у спрайта — низ совпадает с x/y центром на земле.
//
// Эффекты:
//   'slow'  — speedMultiplier × slowFactor пока игрок в зоне.
//   'death' — player.die() при входе (GameScene.updateZoneEffects).
export type ZoneEffectType = 'slow' | 'death';

export class Zone {
  public sprite: Phaser.GameObjects.Arc | Phaser.GameObjects.Sprite;
  public x: number;
  public y: number;
  public radius: number;
  public isDead = false;
  public readonly effectType: ZoneEffectType;
  public readonly slowFactor: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    radius: number,
    color: number,
    slowFactor: number,
    effectType: ZoneEffectType = 'slow',
    textureKey?: string,
    displayDiameterPx?: number,
    lifetimeMs?: number,
  ) {
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.slowFactor = slowFactor;
    this.effectType = effectType;

    if (textureKey && scene.textures.exists(textureKey)) {
      const sp = scene.add.sprite(x, y, textureKey);
      sp.setOrigin(0.5, 0.5);
      // Целевой диаметр в px — для display height. Width растягивается с aspect.
      const targetH = displayDiameterPx ?? radius * 2;
      const aspect = sp.width / sp.height;
      sp.setDisplaySize(targetH * aspect, targetH);
      sp.setDepth(-1);
      // Сохраняем base scale ПОСЛЕ setDisplaySize (она устанавливает scaleX/Y).
      // Иначе pop-in tween к scale 1 сбросил бы display size к нативным размерам.
      const baseScaleX = sp.scaleX;
      const baseScaleY = sp.scaleY;
      sp.setScale(0);
      scene.tweens.add({
        targets: sp,
        scaleX: baseScaleX,
        scaleY: baseScaleY,
        duration: 200,
        ease: 'Cubic.easeOut',
      });
      this.sprite = sp;
    } else {
      const arc = scene.add.circle(x, y, radius, color, 0.5);
      arc.setDepth(-1);
      arc.setScale(0);
      scene.tweens.add({
        targets: arc,
        scale: 1,
        duration: 200,
        ease: 'Cubic.easeOut',
      });
      this.sprite = arc;
    }

    // Авто-уничтожение через lifetimeMs (если задан). Fade-out 500ms перед destroy.
    if (lifetimeMs !== undefined && lifetimeMs > 0) {
      scene.time.delayedCall(lifetimeMs, () => {
        if (this.isDead) return;
        scene.tweens.add({
          targets: this.sprite,
          alpha: 0,
          duration: 500,
          onComplete: () => this.destroy(),
        });
      });
    }
  }

  contains(px: number, py: number): boolean {
    if (this.isDead) return false;
    const dx = px - this.x;
    const dy = py - this.y;
    return dx * dx + dy * dy <= this.radius * this.radius;
  }

  destroy(): void {
    if (this.isDead) return;
    this.isDead = true;
    this.sprite.destroy();
  }
}
